import { DestroyRef, inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, forkJoin, from, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs';
import {
  ApiHandlerService,
  ReDynamicFilters,
  ReLookups,
  ReportTemplate,
  SessionService,
  RulesUsageRequestBodyStrategy,
  TracingInfo,
  TracingInfoService,
  CacheService,
  ReGroupedLookups,
  RawLookupItem,
} from '@everymatrix-base-components/utils';
import { environment } from 'environments/environment.default';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface AdditionalParams {
  [key: string]: any;
}

export interface ReportConfig {
  offset?: number;
  template?: ReportTemplate | null | undefined;
  limit: number;
  allFilters: ReDynamicFilters[];
  formData: AdditionalParams;
  executionType: string;
  sorting?: { field: string | null; value: 'asc' | 'desc' | null };
  exportReportName: string
}

@Injectable({
  providedIn: 'root',
})
export class RulesUsageService {
  destroyRef = inject(DestroyRef);

  constructor(
    private apiHandlerService: ApiHandlerService,
    private sessionService: SessionService,
    private rulesUsageRequestBodyStrategy: RulesUsageRequestBodyStrategy,
    private httpClient: HttpClient,
    private cacheService: CacheService,
    private apiTracingInfo: TracingInfoService,
  ) {}

  runReport(config: ReportConfig): Observable<any> {
    const params = {
      service: 're-usage-report-service/api/v1/report/generate',
    };
    const url = `${this.getBaseURL()}acs-proxy${this.getQueryString(params)}`;

    return from(this.getTracingInfo()).pipe(switchMap((tracingInfo) => this.apiHandlerService
      .Post<any, any>(
        url,
        this.rulesUsageRequestBodyStrategy.buildRequestBody(config, tracingInfo )
      )
      .pipe(
        map((data) => {
          if (!data) {
            throw new Error('Server returned empty response');
          }
          if (
            ((data.errorCode || data.ok === false) && data.message) ||
            (Array.isArray(data) && data[0]?.errorCode)
          ) {
            throw new Error(data.message || data[0]?.message || 'Server responded with an error.');
          }
          return data;
        })
      ))
    )
  }

  async getTracingInfo(): Promise<TracingInfo> {
    try {
      return firstValueFrom(this.apiTracingInfo.tracingInfoData$.pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data !== null),
        take(1)
        )
      )
    } catch (e) {
      return {};
    }
  }

  getSchemas(): Observable<ReDynamicFilters[]> {
    const params = {
      realm: this.sessionService.sessionData?.realm ?? '/',
      service: 're-usage-report-service/api/v1/report/schema',
    };
    const url = `${this.getBaseURL()}acs-proxy${this.getQueryString(params)}`;
    return this.apiHandlerService.Get(url);
  }

  getLookups$(fields: ReDynamicFilters[], selectedParams: AdditionalParams) {
    const apiCalls: { [key: string]: Observable<RawLookupItem[]> } = {};
    const baseUrl = `${this.getBaseURL()}acs-proxy`;
    fields.forEach(
      ({
        field_id,
        field_data_type,
        query_params,
        require_query_params,
        method,
      }) => {
        if (field_data_type == 'enum' && query_params) {
          const params = Object.assign(
            {
              realm: this.sessionService.sessionData?.realm ?? '/',
            },
            query_params
          );
          if (Array.isArray(require_query_params)) {
            require_query_params.forEach((p) => {
              if (selectedParams[p]) params[p] = selectedParams[p];
            });
          }
          const url = `${baseUrl}${this.getQueryString(params)}`;

          if (this.cacheService.has(url)) {
            const cached: any[] = this.cacheService.get(url);
            apiCalls[field_id] = of(structuredClone(cached));
          } else {
            const request$ =
              method === 'GET'
                ? this.apiHandlerService.Get(url)
                : this.apiHandlerService.Post(url, {});

            apiCalls[field_id] = request$.pipe(
              tap((data) => {
                this.cacheService.set(url, structuredClone(data));
              })
            );
          }
        }
      }
    );
    return forkJoin(apiCalls);
  }

  csvLoad(resource: any) {
    const match = resource?.match(/\/([^/]+)\.[^.]+$/);
    const result = match ? match[1] : '';
    if (!result) {
      throw new Error('Failed to fetch file path!');
    }
    
    const url = `${this.getBaseURL()}acs-proxy?namespace=dm-bi-gcs-docs&resourceName=get_google_storage_csv&filePath=${result}`;
    return this.httpClient.post(
      url,
      {
        p_realm: this.sessionService.sessionData?.Domains,
      },
      { responseType: 'text' }
    );
  }

  getQueryString(data: AdditionalParams) {
    let qs = '';
    if (this.isObject(data) && Object.keys(data).length) {
      qs = Object.keys(data).reduce(
        (acc, key) => acc + `${key}=${data[key]}` + '&',
        '?'
      );
      if (qs.endsWith('?')) qs = qs.slice(0, qs.length - 1);
      if (qs.endsWith('&')) qs = qs.slice(0, qs.length - 1);
    }
    return qs;
  }

  isObject(value: any) {
    return value && typeof value === 'object' && !Array.isArray(value);
  }

  getBaseURL() {
    return environment.BASE_ACS_URL;
  }

  getOperatorsList(trees: any = [], selectedRealm: string): ReLookups[] {
    const list: ReLookups[] = [];
    if (!Array.isArray(trees) || !trees.length) return list;
    const operator = (op: any): ReLookups => {
      const { Name, Id, Path } = op;
      return {
        value: Id,
        label: `${Name}` + ' ' + '(' + `${Id}` + ')',
        realm: Path,
        search: (`${Name}` + ' ' + '(' + `${Id}` + ')').toLocaleLowerCase(),
      };
    };

    const flatten = (parent: any) => {
      list.push(operator(parent));
      if (parent.Children && parent.Children.length) {
        parent.Children.forEach((child: any) => {
          flatten(child);
        });
      }
    };

    trees.forEach((tree) => flatten(tree));
    return list.filter(({ realm }) => realm?.includes(selectedRealm));
  }

  mapLookups(lookups: RawLookupItem[]): ReLookups[] {
    let sorting = true;

    const arr: ReLookups[] = Array.isArray(lookups)
      ? lookups.reduce(
          (acc, { id, name, refId, rank, friendlyName, actionTypeId }) => {
            const value = refId ?? id ?? actionTypeId;
            const label = name ?? friendlyName;

            if (value == null || label == null) return acc;

            const search = label.toLowerCase();

            const rowLookup: ReLookups = {
              value,
              label,
              search,
            };

            if (typeof rank === 'number') {
              rowLookup.rank = rank;
            } else {
              sorting = false;
            }

            acc.push(rowLookup);
            return acc;
          },
          [] as ReLookups[]
        )
      : [];

    if (sorting) {
      arr.sort((a, b) => (a.rank! > b.rank! ? 1 : -1));
    }

    return arr;
  }

  mapGroupedLookups(lookups: RawLookupItem[]): ReGroupedLookups[] {
    if (!Array.isArray(lookups) || !lookups.length) return [];

    const grouped = new Map<string, { rank: number; items: ReLookups[] }>();

    for (const item of lookups) {
      const hasCategory =
        Array.isArray(item.category) && item.category.length > 0;
      const category: { name: string; rank: number }[] = hasCategory
        ? item.category!
        : [];

      const groupKey = category.map((c) => c.name).join(' / ');
      const groupRank = category.reduce(
        (acc, { rank }, idx) => acc + (rank - idx),
        0
      );

      const value = item.refId ?? item.id ?? item.actionTypeId;
      const label = item.name ?? item.friendlyName;

      if (value == null || label == null) continue;

      const search = `${category
        .map((c) => c.name.toLowerCase())
        .join(' ')} ${label.toLowerCase()}`.trim();

      const reItem: ReLookups = {
        value,
        label,
        search,
      };

      if (typeof item.rank === 'number') {
        reItem.rank = item.rank;
      }

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, { rank: groupRank, items: [] });
      }

      grouped.get(groupKey)!.items.push(reItem);
    }

    const result: ReGroupedLookups[] = Array.from(grouped.entries())
      .map(([key, group]) => ({
        label: key,
        value: key,
        rank: group.rank,
        items: group.items.sort(
          (a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity)
        ),
      }))
      .sort((a, b) => a.rank - b.rank) // sort groups by rank
      .map(({ ...group }) => group); // remove internal rank

    return result;
  }
}